<script setup>
import { ref, computed, onMounted } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useToggle } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useEmitter } from 'dashboard/composables/emitter';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useConversationRequiredAttributes } from 'dashboard/composables/useConversationRequiredAttributes';

import WootDropdownItem from 'shared/components/ui/dropdown/DropdownItem.vue';
import WootDropdownMenu from 'shared/components/ui/dropdown/DropdownMenu.vue';
import wootConstants from 'dashboard/constants/globals';
import ConversationApi from 'dashboard/api/inbox/conversation';
import TeamsAPI from 'dashboard/api/teams';
import {
  CMD_REOPEN_CONVERSATION,
  CMD_RESOLVE_CONVERSATION,
} from 'dashboard/helper/commandbar/events';

import ButtonGroup from 'dashboard/components-next/buttonGroup/ButtonGroup.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ConversationResolveAttributesModal from 'dashboard/components-next/ConversationWorkflow/ConversationResolveAttributesModal.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import {
  CAUSE_REASONS,
  CAUSE_REQUIRED_TEAM,
} from 'dashboard/constants/causeReasons';

const store = useStore();
const getters = useStoreGetters();
const { t } = useI18n();
const { checkMissingAttributes } = useConversationRequiredAttributes();

const arrowDownButtonRef = ref(null);
const isLoading = ref(false);
const resolveAttributesModalRef = ref(null);

const [showActionsDropdown, toggleDropdown] = useToggle();
const closeDropdown = () => toggleDropdown(false);
const openDropdown = () => toggleDropdown(true);

const currentChat = computed(() => getters.getSelectedChat.value);

const isOpen = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.OPEN
);
const isPending = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.PENDING
);
const isResolved = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.RESOLVED
);
const isSnoozed = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.SNOOZED
);

// Fork JavaMifi: resolve per tim. Tim cuma bisa ditandai anggotanya (atau admin);
// status percakapan (resolve all, pending, snooze) cuma oleh assignee (atau admin).
const currentUser = computed(() => getters.getCurrentUser.value);
const isAdmin = computed(
  () => getters.getCurrentRole.value === 'administrator'
);
// Fork JavaMifi: daftar tim di store bisa kosong untuk agen participating-only (halaman yang memuatnya tidak
// dibuka, dan cache IndexedDB teams/get bisa menggantung). Ambil keanggotaan tim langsung dari API tanpa cache.
const myTeamIdsFromApi = ref(null);
onMounted(async () => {
  try {
    const { data } = await TeamsAPI.get(false);
    myTeamIdsFromApi.value = data
      .filter(team => team.is_member)
      .map(team => team.id);
  } catch {
    myTeamIdsFromApi.value = null;
  }
});
const myTeamIds = computed(
  () =>
    myTeamIdsFromApi.value ??
    getters['teams/getMyTeams'].value.map(team => team.id)
);
const teamResolutions = computed(
  () => currentChat.value.team_resolutions || []
);
const canChangeStatus = computed(() => {
  if (isAdmin.value) return true;
  const assigneeId = currentChat.value.meta?.assignee?.id;
  if (assigneeId) return assigneeId === currentUser.value.id;
  return currentUser.value.custom_attributes?.participating_only !== true;
});
// Resolve all dikunci selama masih ada tim pending (administrator boleh memaksa; server ikut menolak).
const pendingTeamNames = computed(() =>
  teamResolutions.value.filter(team => !team.resolved).map(t2 => t2.team_name)
);
const isResolveBlocked = computed(
  () => !isAdmin.value && pendingTeamNames.value.length > 0
);
const resolveBlockedMessage = computed(() =>
  isResolveBlocked.value
    ? t('CONVERSATION.RESOLVE_DROPDOWN.WAITING_TEAMS', {
        teams: pendingTeamNames.value.join(', '),
      })
    : ''
);
// Header sempit: tampilkan 1 tim (pending lebih dulu), sisanya "+N more" dengan tooltip.
const sortedTeams = computed(() =>
  [...teamResolutions.value].sort((a, b) => a.resolved - b.resolved)
);
const visibleTeams = computed(() => sortedTeams.value.slice(0, 1));
const hiddenTeams = computed(() => sortedTeams.value.slice(1));
const teamLabel = team =>
  team.resolved
    ? t('CONVERSATION.RESOLVE_DROPDOWN.TEAM_DONE', { team: team.team_name })
    : t('CONVERSATION.RESOLVE_DROPDOWN.TEAM_PENDING', { team: team.team_name });
const canResolveTeam = teamId =>
  isAdmin.value || myTeamIds.value.includes(teamId);

const showAdditionalActions = computed(
  () =>
    (canChangeStatus.value && !isPending.value && !isSnoozed.value) ||
    teamResolutions.value.length > 0
);

const showOpenButton = computed(() => {
  return isPending.value || isSnoozed.value;
});

const sendTeamResolution = async (team, resolution = '', causeReason = '') => {
  isLoading.value = true;
  try {
    const { data } = await ConversationApi.toggleTeamResolution({
      conversationId: currentChat.value.id,
      teamId: team.team_id,
      resolved: !team.resolved,
      resolution,
      causeReason,
      causeReasonLabel:
        CAUSE_REASONS.find(option => option.value === causeReason)?.label || '',
    });
    store.commit('UPDATE_CONVERSATION', {
      id: currentChat.value.id,
      team_resolutions: data.team_resolutions,
      updated_at: currentChat.value.updated_at,
    });
    useAlert(t('CONVERSATION.RESOLVE_DROPDOWN.TEAM_UPDATED'));
    return true;
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('CONVERSATION.RESOLVE_DROPDOWN.TEAM_UPDATE_FAILED')
    );
    return false;
  } finally {
    isLoading.value = false;
  }
};

// Fork JavaMifi: resolve tim wajib menulis resolusi (hasil kerja tim); buka lagi tim tidak perlu.
const teamResolutionDialogRef = ref(null);
const teamToResolve = ref(null);
const teamResolutionText = ref('');
// Fork JavaMifi: penyebab (cause_or_reason NocoBase) wajib dipilih saat resolve tim teknikal.
const teamCauseReason = ref('');
const needsCauseReason = computed(
  () => teamToResolve.value?.team_name === CAUSE_REQUIRED_TEAM
);
const canConfirmTeamResolution = computed(
  () =>
    Boolean(teamResolutionText.value.trim()) &&
    (!needsCauseReason.value || Boolean(teamCauseReason.value))
);

const toggleTeamResolution = team => {
  closeDropdown();
  if (team.resolved) {
    sendTeamResolution(team);
    return;
  }
  teamToResolve.value = team;
  teamResolutionText.value = '';
  teamCauseReason.value = '';
  teamResolutionDialogRef.value?.open();
};

const confirmTeamResolution = async () => {
  if (!canConfirmTeamResolution.value || !teamToResolve.value) return;
  const ok = await sendTeamResolution(
    teamToResolve.value,
    teamResolutionText.value.trim(),
    needsCauseReason.value ? teamCauseReason.value : ''
  );
  if (ok) teamResolutionDialogRef.value?.close();
};

const getConversationParams = () => {
  const allConversations = document.querySelectorAll(
    '.conversations-list .conversation'
  );

  const activeConversation = document.querySelector(
    'div.conversations-list div.conversation.active'
  );
  const activeConversationIndex = [...allConversations].indexOf(
    activeConversation
  );
  const lastConversationIndex = allConversations.length - 1;

  return {
    all: allConversations,
    activeIndex: activeConversationIndex,
    lastIndex: lastConversationIndex,
  };
};

const openSnoozeModal = () => {
  const ninja = document.querySelector('ninja-keys');
  ninja.open({ parent: 'snooze_conversation' });
};

const toggleStatus = (status, snoozedUntil, customAttributes = null) => {
  closeDropdown();
  isLoading.value = true;

  const payload = {
    conversationId: currentChat.value.id,
    status,
    snoozedUntil,
  };

  if (customAttributes) {
    payload.customAttributes = customAttributes;
  }

  store
    .dispatch('toggleStatus', payload)
    .then(() => {
      useAlert(t('CONVERSATION.CHANGE_STATUS'));
    })
    .catch(error => {
      // Fork JavaMifi: tampilkan alasan penolakan server (mis. masih menunggu tim).
      useAlert(error?.response?.data?.error || t('CONVERSATION.CHANGE_STATUS'));
    })
    .finally(() => {
      isLoading.value = false;
    });
};

const handleResolveWithAttributes = ({ attributes, context }) => {
  if (context) {
    const currentCustomAttributes = currentChat.value.custom_attributes || {};
    const mergedAttributes = { ...currentCustomAttributes, ...attributes };
    toggleStatus(
      wootConstants.STATUS_TYPE.RESOLVED,
      context.snoozedUntil,
      mergedAttributes
    );
  }
};

const onCmdOpenConversation = () => {
  toggleStatus(wootConstants.STATUS_TYPE.OPEN);
};

const onCmdResolveConversation = () => {
  if (isResolveBlocked.value) {
    useAlert(resolveBlockedMessage.value);
    return;
  }
  const currentCustomAttributes = currentChat.value.custom_attributes || {};
  const { hasMissing, missing } = checkMissingAttributes(
    currentCustomAttributes
  );

  if (hasMissing) {
    const conversationContext = {
      id: currentChat.value.id,
      snoozedUntil: null,
    };
    resolveAttributesModalRef.value?.open(
      missing,
      currentCustomAttributes,
      conversationContext
    );
  } else {
    toggleStatus(wootConstants.STATUS_TYPE.RESOLVED);
  }
};

const keyboardEvents = {
  'Alt+KeyM': {
    action: () => arrowDownButtonRef.value?.$el.click(),
    allowOnFocusedInput: true,
  },
  'Alt+KeyE': {
    action: async () => {
      onCmdResolveConversation();
    },
  },
  '$mod+Alt+KeyE': {
    action: async event => {
      const { all, activeIndex, lastIndex } = getConversationParams();
      onCmdResolveConversation();

      if (activeIndex < lastIndex) {
        all[activeIndex + 1].click();
      } else if (all.length > 1) {
        all[0].click();
        document.querySelector('.conversations-list').scrollTop = 0;
      }
      event.preventDefault();
    },
  },
};

useKeyboardEvents(keyboardEvents);

useEmitter(CMD_REOPEN_CONVERSATION, onCmdOpenConversation);
useEmitter(CMD_RESOLVE_CONVERSATION, onCmdResolveConversation);
</script>

<template>
  <div
    class="flex relative justify-end items-center gap-2 min-w-0 resolve-actions"
  >
    <div
      v-if="teamResolutions.length"
      class="flex items-center gap-2 min-w-0 whitespace-nowrap"
    >
      <span
        v-for="team in visibleTeams"
        :key="team.team_id"
        class="flex items-center gap-1 text-xs min-w-0"
        :class="team.resolved ? 'text-n-teal-11' : 'text-n-slate-11'"
        :title="
          team.resolved
            ? [team.resolved_by, team.cause_reason_label, team.resolution]
                .filter(Boolean)
                .join(': ')
            : ''
        "
      >
        <span
          class="size-3.5 flex-shrink-0"
          :class="
            team.resolved ? 'i-lucide-circle-check' : 'i-lucide-circle-dashed'
          "
        />
        <span class="truncate">{{ teamLabel(team) }}</span>
      </span>
      <span
        v-if="hiddenTeams.length"
        v-tooltip.bottom="hiddenTeams.map(teamLabel).join(', ')"
        class="flex-shrink-0 px-1.5 py-0.5 text-xs rounded-md bg-n-alpha-2 text-n-slate-11 cursor-default"
      >
        {{
          t('CONVERSATION.RESOLVE_DROPDOWN.TEAM_MORE', {
            count: hiddenTeams.length,
          })
        }}
      </span>
    </div>
    <ButtonGroup
      class="flex-shrink-0 rounded-lg shadow outline-1 outline"
      :class="!showOpenButton ? 'outline-n-container' : 'outline-transparent'"
    >
      <Button
        v-if="canChangeStatus && isOpen"
        v-tooltip.bottom="resolveBlockedMessage"
        :label="
          teamResolutions.length
            ? t('CONVERSATION.HEADER.RESOLVE_ALL_ACTION')
            : t('CONVERSATION.HEADER.RESOLVE_ACTION')
        "
        size="sm"
        color="slate"
        no-animation
        class="ltr:rounded-r-none rtl:rounded-l-none !outline-0"
        :disabled="isResolveBlocked"
        :is-loading="isLoading"
        @click="onCmdResolveConversation"
      />
      <Button
        v-else-if="canChangeStatus && isResolved"
        :label="t('CONVERSATION.HEADER.REOPEN_ACTION')"
        size="sm"
        color="slate"
        no-animation
        class="ltr:rounded-r-none rtl:rounded-l-none !outline-0"
        :is-loading="isLoading"
        @click="onCmdOpenConversation"
      />
      <Button
        v-else-if="canChangeStatus && showOpenButton"
        :label="t('CONVERSATION.HEADER.OPEN_ACTION')"
        size="sm"
        color="slate"
        no-animation
        :is-loading="isLoading"
        @click="onCmdOpenConversation"
      />
      <Button
        v-if="showAdditionalActions"
        ref="arrowDownButtonRef"
        icon="i-lucide-chevron-down"
        :disabled="isLoading"
        size="sm"
        no-animation
        class="ltr:rounded-l-none rtl:rounded-r-none !outline-0"
        color="slate"
        trailing-icon
        @click="openDropdown"
      />
    </ButtonGroup>
    <div
      v-if="showActionsDropdown"
      v-on-clickaway="closeDropdown"
      class="border rounded-lg shadow-lg border-n-strong dark:border-n-strong box-content p-2 w-fit z-10 bg-n-alpha-3 backdrop-blur-[100px] absolute block left-auto top-full mt-0.5 start-0 xl:start-auto xl:end-0 max-w-[12.5rem] min-w-[9.75rem] [&_ul>li]:mb-0"
    >
      <WootDropdownMenu class="mb-0">
        <WootDropdownItem v-for="team in teamResolutions" :key="team.team_id">
          <Button
            :label="
              team.resolved
                ? t('CONVERSATION.RESOLVE_DROPDOWN.REOPEN_TEAM', {
                    team: team.team_name,
                  })
                : t('CONVERSATION.RESOLVE_DROPDOWN.RESOLVE_TEAM', {
                    team: team.team_name,
                  })
            "
            ghost
            slate
            sm
            start
            :icon="team.resolved ? 'i-lucide-rotate-ccw' : 'i-lucide-check'"
            class="w-full"
            :disabled="!canResolveTeam(team.team_id)"
            @click="() => toggleTeamResolution(team)"
          />
        </WootDropdownItem>
        <WootDropdownItem v-if="canChangeStatus && !isPending">
          <Button
            :label="t('CONVERSATION.RESOLVE_DROPDOWN.SNOOZE_UNTIL')"
            ghost
            slate
            sm
            start
            icon="i-lucide-alarm-clock-minus"
            class="w-full"
            @click="() => openSnoozeModal()"
          />
        </WootDropdownItem>
        <WootDropdownItem v-if="canChangeStatus && !isPending">
          <Button
            :label="t('CONVERSATION.RESOLVE_DROPDOWN.MARK_PENDING')"
            ghost
            slate
            sm
            start
            icon="i-lucide-circle-dot-dashed"
            class="w-full"
            @click="() => toggleStatus(wootConstants.STATUS_TYPE.PENDING)"
          />
        </WootDropdownItem>
      </WootDropdownMenu>
    </div>
    <ConversationResolveAttributesModal
      ref="resolveAttributesModalRef"
      @submit="handleResolveWithAttributes"
    />
    <Dialog
      ref="teamResolutionDialogRef"
      :title="
        t('CONVERSATION.RESOLVE_DROPDOWN.TEAM_RESOLUTION_TITLE', {
          team: teamToResolve?.team_name || '',
        })
      "
      :description="t('CONVERSATION.RESOLVE_DROPDOWN.TEAM_RESOLUTION_DESC')"
      :confirm-button-label="
        t('CONVERSATION.RESOLVE_DROPDOWN.TEAM_RESOLUTION_CONFIRM')
      "
      :disable-confirm-button="!canConfirmTeamResolution"
      :is-loading="isLoading"
      @confirm="confirmTeamResolution"
    >
      <div v-if="needsCauseReason" class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('CONVERSATION.RESOLVE_DROPDOWN.TEAM_CAUSE_LABEL') }}
        </label>
        <Select
          v-model="teamCauseReason"
          class="!w-full [&>select]:w-full"
          :options="CAUSE_REASONS"
          :placeholder="
            t('CONVERSATION.RESOLVE_DROPDOWN.TEAM_CAUSE_PLACEHOLDER')
          "
        />
      </div>
      <TextArea
        v-model="teamResolutionText"
        :placeholder="
          t('CONVERSATION.RESOLVE_DROPDOWN.TEAM_RESOLUTION_PLACEHOLDER')
        "
        :max-length="1000"
        auto-height
        show-character-count
      />
    </Dialog>
  </div>
</template>
